import { Controller } from "@hotwired/stimulus"

const EDGE_MARGIN = 12

export default class extends Controller {
  static targets = ["handle"]

  connect() {
    this.dragging = false
    this.move = this.move.bind(this)
    this.stop = this.stop.bind(this)
  }

  disconnect() {
    this.removeWindowListeners()
  }

  start(event) {
    if (event.button !== 0 || this.shouldIgnoreDrag(event.target)) return

    const rect = this.element.getBoundingClientRect()
    this.dragging = true
    this.pointerOffsetX = event.clientX - rect.left
    this.pointerOffsetY = event.clientY - rect.top

    this.element.classList.add("is-dragging")
    this.element.style.setProperty("--admin-draggable-left", `${rect.left}px`)
    this.element.style.setProperty("--admin-draggable-top", `${rect.top}px`)
    this.element.style.setProperty("--admin-draggable-transform", "none")

    this.capturePointer(event)

    window.addEventListener("pointermove", this.move)
    window.addEventListener("pointerup", this.stop, { once: true })
    window.addEventListener("pointercancel", this.stop, { once: true })
    event.preventDefault()
  }

  move(event) {
    if (!this.dragging) return

    const rect = this.element.getBoundingClientRect()
    const left = this.clamp(event.clientX - this.pointerOffsetX, EDGE_MARGIN, window.innerWidth - rect.width - EDGE_MARGIN)
    const top = this.clamp(event.clientY - this.pointerOffsetY, EDGE_MARGIN, window.innerHeight - rect.height - EDGE_MARGIN)

    this.element.style.setProperty("--admin-draggable-left", `${left}px`)
    this.element.style.setProperty("--admin-draggable-top", `${top}px`)
  }

  stop(event) {
    if (!this.dragging) return

    this.dragging = false
    this.element.classList.remove("is-dragging")

    this.releasePointer(event)

    this.removeWindowListeners()
  }

  shouldIgnoreDrag(target) {
    if (!(target instanceof Element)) return false

    return target.closest("a, button, input, label, select, textarea, [data-admin-draggable-ignore]")
  }

  capturePointer(event) {
    if (!this.hasHandleTarget || !this.handleTarget.setPointerCapture) return

    try {
      this.handleTarget.setPointerCapture(event.pointerId)
    } catch (_) {
      // Pointer capture is best-effort; window listeners still keep dragging usable.
    }
  }

  releasePointer(event) {
    if (!this.hasHandleTarget || !this.handleTarget.releasePointerCapture) return

    try {
      this.handleTarget.releasePointerCapture(event.pointerId)
    } catch (_) {
      // Ignore capture differences between browsers and automation environments.
    }
  }

  clamp(value, min, max) {
    if (max < min) return min

    return Math.min(Math.max(value, min), max)
  }

  removeWindowListeners() {
    window.removeEventListener("pointermove", this.move)
    window.removeEventListener("pointerup", this.stop)
    window.removeEventListener("pointercancel", this.stop)
  }
}
